<template>
    <div>
        <div class="pb-4">
            <p class="block text-xs font-semibold mb-1">Account Holder Name:</p>
            <p class="text-xs">{{ policyName }}</p>
        </div>
        <template v-if="hasIbanDetails">
            <div class="">
                <div class="pb-4">
                    <label class="block text-base font-semibold mb-1" for="iban"
                        >IBAN
                        <p class="text-xs mb-2 font-normal">
                            You can find your IBAN on the transaction page of your bank statement or in your mobile
                            banking app.
                        </p>
                    </label>
                    <asli-text-input
                        id="iban"
                        ref="ibanInputRef"
                        v-model="directDebitDetails.iban"
                        @validated="handleValidated('iban', true)"
                        @input="handleValidated('iban', false)"
                        autocomplete="on"
                        :rules="`required:iban|iban`"
                        class="mr-2 gb_mask"
                        flex-width
                        no-margin
                        :is-upper-cased="true"
                        :mask="ALPHA_NUMERIC_IBAN_MASK"
                    />
                    <p v-if="errorMessage" id="error-message" class="font-semibold text-[#EF4444] mt-1.5">
                        {{ errorMessage }}
                    </p>
                    <p class="text-xs pt-4">
                        Don't know your IBAN? Enter your
                        <button @click="toggleIbanDetails" class="underline text-green-500">
                            sort code and account number
                        </button>
                        instead
                    </p>
                </div>
            </div>
        </template>
        <template v-else>
            <div class="max-w-full">
                <div class="grid grid-cols-1 md:grid-cols-2 md:gap-4">
                    <asli-text-input
                        id="sort_code"
                        name="sort_code"
                        v-model="directDebitDetails.sort_code"
                        @validated="handleValidated('sort_code', true)"
                        @input="handleValidated('sort_code', false)"
                        rules="required:sort_code|sortCode"
                        label="Sort Code"
                        mask="##-##-##"
                        flex-width
                        no-margin
                    />
                    <asli-text-input
                        id="account_number"
                        name="account_number"
                        v-model="directDebitDetails.account_number"
                        @validated="handleValidated('account_number', true)"
                        @input="handleValidated('account_number', false)"
                        label="Account Number"
                        mask="########"
                        rules="required:account_number|accountNumber"
                        flex-width
                        no-margin
                    />
                </div>
                <p v-if="errorMessage" id="error-message" class="font-semibold text-[#EF4444] mt-1.5">
                    {{ errorMessage }}
                </p>
                <p class="text-xs py-4">
                    Don't know your sort code and account number?
                    <br />
                    Enter your
                    <button @click="toggleIbanDetails" class="underline text-green-500">IBAN</button>
                    instead
                </p>
            </div>
        </template>
        <p class="border-y-2 border-grey-200 py-4 text-xs">
            Please note, if you would like to pay for your Home Insurance from a SEPA bank account outside of Ireland,
            please contact our call centre on 0818 22 22 22 and we will arrange this with you.
        </p>
        <div class="flex flex-col border-b-2 border-grey-200 py-4 text-xs gap-y-2">
            <p>The terms and conditions of the premium payment plan will be sent to you with your quote documents.</p>
            <p>
                If your policy is cancelled by either party, any additional discount will be removed, and your pro rata
                amount due will be based on your full premium.
            </p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { AsliTextInput, ALPHA_NUMERIC_IBAN_MASK } from "@mbs-npm/anpost-ui";
import { onMounted, ref, watch } from "vue";
import { DirectDebit } from "@/stores/risk/types/types";

const props = defineProps<{
    policyName: string;
    directDebitDetails: DirectDebit;
    errorMessage: string | null;
}>();

const isChecked = ref(false);
const hasIbanDetails = ref(true);
const ibanInputRef = ref<InstanceType<typeof AsliTextInput> | null>(null);

const emit = defineEmits<{
    (e: "updateErrorMessage", value: string | null): void;
    (e: "updateDirectDebitDetails", value: DirectDebit): void;
    (e: "isChecked", value: boolean): void;
    (e: "validated", field: keyof DirectDebit, valid: boolean): void;
}>();

watch(isChecked, newVal => {
    emit("isChecked", newVal);
});

function toggleIbanDetails() {
    hasIbanDetails.value = !hasIbanDetails.value;
    emit("updateErrorMessage", null);
    if (!hasIbanDetails.value) {
        isChecked.value = false;
        props.directDebitDetails.iban = "";
    } else {
        emit("isChecked", true);
    }
}

function handleValidated(field: keyof DirectDebit, valid: boolean) {
    emit("validated", field, valid);
}

onMounted(() => {
    if (props.directDebitDetails.iban?.length) ibanInputRef.value?.blurInput();
});
</script>
